// src/ui/PlatoScraper.tsx

import React, { useEffect, useMemo, useState } from "react";
import { useUser } from "../lib/UserContext";
import { supabase, getAuthToken } from "../lib/supabase";

// ─── Types ────────────────────────────────────────────────────────────────────

type OnlineRule = {
  id: string;
  name: string;
  book_calendar?: string;
  [key: string]: any;
};

type OnlineCalendar = {
  id: string;
  name: string;
  rules?: OnlineRule[];
  [key: string]: any;
};

type OnlineConfig = {
  key: string;
  active: boolean;
  calendars: OnlineCalendar[];
  [key: string]: any;
};

type ClientOption = {
  id: string;
  raison_sociale: string;
};

type CalendarItem = {
  id: string;
  name: string;
  raw?: Record<string, any>;
};

type AppointmentItem = {
  id: string;
  calendar_color?: string;
  calendar_name?: string;
  patient_id: string;
  patient_name: string;
  start: string;
  end: string;
  phone: string;
  practitioner?: string;
  all_day?: number;
  source?: string;
  appointment_status?: string;
  raw?: Record<string, any>;
};

type MergedAppointmentRow = {
  key: string;
  patient_name: string;
  phone: string;
  time: string;
  sortStart: string;
  durationMinutes: number;
  calendars: string[];
  // champs bruts pour pre-remplir le formulaire d'édition
  raw_id?: string;
  raw_calendar_id?: string;
  raw_patient_id?: string;
  raw_calendar_color?: string;
  raw_status_code?: string;
};

type ApptFormData = {
  calendar_id: string;
  patient_id: string;
  title: string;
  starttime: string; // format "YYYY-MM-DDTHH:MM" pour datetime-local
  endtime: string;
  color: string;
  status_code: string;
};

const EMPTY_FORM: ApptFormData = {
  calendar_id: "",
  patient_id: "",
  title: "",
  starttime: "",
  endtime: "",
  color: "",
  status_code: "",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseDateSafely(value: string): Date | null {
  if (!value) return null;
  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function formatDurationFromMinutes(totalMinutes: number): string {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return "-";
  if (totalMinutes < 60) return `${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}` : `${hours}h`;
}

function getDurationMinutes(start?: string, end?: string): number {
  if (!start || !end) return 0;
  const startDate = parseDateSafely(start);
  const endDate = parseDateSafely(end);
  if (!startDate || !endDate) return 0;
  const diffMs = endDate.getTime() - startDate.getTime();
  if (!Number.isFinite(diffMs) || diffMs <= 0) return 0;
  return Math.round(diffMs / 60000);
}

function normalizePatientKey(name?: string, phone?: string): string {
  const normalizedName = (name || "").trim().toLowerCase().replace(/\s+/g, " ");
  const normalizedPhone = (phone || "").replace(/\D+/g, "");
  return `${normalizedName}::${normalizedPhone}`;
}

function formatTime(value: string): string {
  if (!value) return "-";
  const parsed = parseDateSafely(value);
  if (!parsed || Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// ─────────────────────────────────────────────────────────────────────────────

const BACKEND = import.meta.env.VITE_API_URL || "https://whatsapp-healthcare.onrender.com";

export default function PlatoScraper() {
  const user = useUser();

  // ── Shared state ─────────────────────────────────────────────────────────
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [dbName, setDbName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [activeTab, setActiveTab] = useState<"slots" | "calendars">("slots");

  // ── Tab 1 : Slot Tester ───────────────────────────────────────────────────
  const [onlineConfig, setOnlineConfig] = useState<OnlineConfig | null>(null);
  const [selectedCalendarId, setSelectedCalendarId] = useState("");
  const [startDate, setStartDate] = useState(
    () => new Date().toISOString().split("T")[0]
  );
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [bookingUrl, setBookingUrl] = useState<string | null>(null);
  const [onlineRaw, setOnlineRaw] = useState<any | null>(null);

  // ── Tab 2 : Calendars & Appointments ─────────────────────────────────────
  const [calItems, setCalItems] = useState<CalendarItem[]>([]);
  const [calLoading, setCalLoading] = useState(false);
  const [calError, setCalError] = useState("");
  const [calSource, setCalSource] = useState("");
  const [apptDate, setApptDate] = useState(
    () => new Date().toISOString().split("T")[0]
  );
  const [apptItems, setApptItems] = useState<AppointmentItem[]>([]);
  const [apptLoading, setApptLoading] = useState(false);
  const [apptError, setApptError] = useState("");
  const [apptAllowedColors, setApptAllowedColors] = useState<string[]>([]);

  // ── Tab 2 : Raw Field Inspector ───────────────────────────────────────────
  const [inspectedApptId, setInspectedApptId] = useState<string | null>(null);
  const [apptDetailRaw, setApptDetailRaw] = useState<Record<string, any> | null>(null);
  const [apptDetailLoading, setApptDetailLoading] = useState(false);

  // ── Status map clinique ───────────────────────────────────────────────────
  const [statusField, setStatusField] = useState("");
  const [statusMap, setStatusMap] = useState<Record<string, string>>({});

  // ── Formulaire CRUD appointment ───────────────────────────────────────────
  const [apptFormOpen, setApptFormOpen] = useState(false);
  const [apptFormMode, setApptFormMode] = useState<"create" | "edit">("create");
  const [apptFormData, setApptFormData] = useState<ApptFormData>(EMPTY_FORM);
  const [editingApptId, setEditingApptId] = useState<string | null>(null);
  const [apptFormLoading, setApptFormLoading] = useState(false);
  const [apptFormError, setApptFormError] = useState("");

  // ─────────────────────────────────────────────
  // 1) Charger les cliniques (clients)
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const loadClients = async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("id, raison_sociale")
        .order("raison_sociale", { ascending: true });

      if (!error && data) {
        setClients(data as ClientOption[]);
      }
    };
    loadClients();
  }, [user]);

  // ─────────────────────────────────────────────
  // 2) Charger la config Plato pour une clinique
  // ─────────────────────────────────────────────
  const loadPlatoConfigForClient = async (clientId: string) => {
    setError(null);
    setOnlineConfig(null);
    setSlots(null);
    setDbName("");
    setApiKey("");
    // Reset tab 2
    setCalItems([]);
    setCalError("");
    setCalSource("");
    setApptItems([]);
    setApptError("");
    setApptAllowedColors([]);
    setStatusField("");
    setStatusMap({});

    if (!clientId) return;

    const { data, error } = await supabase
      .from("clinic_knowledge")
      .select("key, value")
      .eq("praticien_id", clientId)
      .in("key", ["plato_db_name", "plato_api_key"]);

    if (error) {
      console.error("[FRONT][PLATO_CONFIG][ERROR]", error);
      setError("Erreur lors du chargement de la configuration Plato.");
      return;
    }

    const rows = data || [];
    if (!rows.length) {
      setError("Aucune configuration Plato trouvée pour cette clinique.");
      return;
    }

    const mapping: Record<string, string> = {};
    rows.forEach((r: any) => {
      mapping[r.key] = r.value;
    });

    const db = mapping["plato_db_name"];
    const key = mapping["plato_api_key"];

    if (!db || !key) {
      setError("Configuration Plato incomplète (db_name ou api_key manquants).");
      return;
    }

    setDbName(db);
    setApiKey(key);

    // Charger status field + status map depuis la table clients
    const { data: clientData } = await supabase
      .from("clients")
      .select("plato_appt_status_field, plato_appt_status_map")
      .eq("id", clientId)
      .single();

    if (clientData) {
      setStatusField(String(clientData.plato_appt_status_field || ""));
      const rawMap = clientData.plato_appt_status_map;
      if (rawMap && typeof rawMap === "object") {
        setStatusMap(rawMap as Record<string, string>);
      } else if (typeof rawMap === "string" && rawMap) {
        try { setStatusMap(JSON.parse(rawMap)); } catch { setStatusMap({}); }
      }
    }
  };

  const handleSelectClient = async (clientId: string) => {
    setSelectedClientId(clientId);
    await loadPlatoConfigForClient(clientId);
  };

  // ─────────────────────────────────────────────
  // Tab 1 — /plato/online_list
  // ─────────────────────────────────────────────
  const fetchBookingLink = async () => {
    try {
      setBookingUrl(null);
      setOnlineRaw(null);

      const res = await fetch(`${BACKEND}/plato/online_list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ db_name: dbName, api_key: apiKey }),
      });

      const data = await res.json();
      console.log("[FRONT][ONLINE RAW]", data);
      setOnlineRaw(data);

      const key = data?.key;
      const slug = data?.public_slug || data?.slug;
      const bookingUrlFromApi = data?.booking_url;

      if (bookingUrlFromApi) {
        setBookingUrl(bookingUrlFromApi);
        return;
      }

      if (key && slug) {
        setBookingUrl(`https://clinic.platomedical.com/book/${slug}/${key}`);
        return;
      }

      setBookingUrl("❌ Impossible de construire l'URL (key / slug manquants)");
    } catch (err) {
      console.error(err);
      setBookingUrl("❌ Erreur lors de la récupération du lien");
    }
  };

  const selectedCalendar: OnlineCalendar | undefined = useMemo(() => {
    if (!onlineConfig) return undefined;
    return onlineConfig.calendars?.find((c) => c.id === selectedCalendarId);
  }, [onlineConfig, selectedCalendarId]);

  const handleLoadOnlineConfig = async () => {
    setError(null);
    setOnlineConfig(null);
    setSlots(null);

    let online = null;
    try {
      const res = await fetch(`${BACKEND}/plato/online_list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ db_name: dbName, api_key: apiKey }),
      });

      online = await res.json();
      console.log("[FRONT] ONLINE LIST =", online);

      if (online.active && online.calendars?.length > 0) {
        setOnlineConfig(online);
        setSelectedCalendarId(online.calendars[0].id);
        return;
      }
    } catch (err) {
      console.error("[FRONT][ONLINE][ERROR]", err);
    }

    try {
      const resCal = await fetch(`${BACKEND}/plato/list_calendars`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ db_name: dbName, api_key: apiKey }),
      });

      const calData = await resCal.json();
      console.log("[FRONT] APPOINTMENT CALENDARS =", calData);

      if (!resCal.ok || !calData.calendars?.length) {
        throw new Error("No calendars returned by Plato.");
      }

      const fakeConfig = { active: false, calendars: calData.calendars };
      setOnlineConfig(fakeConfig);
      setSelectedCalendarId(calData.calendars[0].id);
    } catch (err: any) {
      console.error("[FRONT][CALENDAR_FALLBACK][ERROR]", err);
      setError("Impossible de récupérer les calendars Plato.");
    }
  };

  const groupSlots = (rawSlots: any[]) => {
    const grouped: Record<string, Record<string, string[]>> = {};

    rawSlots.forEach((slot) => {
      const doctor =
        slot.doctor_name || slot.doctor || slot.rule_name || "Unknown Doctor";
      const date = slot.date;
      const time = slot.time;

      if (!date || !time) return;

      if (!grouped[doctor]) grouped[doctor] = {};
      if (!grouped[doctor][date]) grouped[doctor][date] = [];

      grouped[doctor][date].push(time);
    });

    Object.keys(grouped).forEach((doctor) => {
      Object.keys(grouped[doctor]).forEach((date) => {
        grouped[doctor][date].sort();
      });
    });

    return grouped;
  };

  const handleFetchSlots = async () => {
    setLoading(true);
    setError(null);
    setSlots(null);

    try {
      const month = startDate.slice(0, 7);

      const res = await fetch(`${BACKEND}/plato/test_manual`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          db_name: dbName,
          api_key: apiKey,
          calendar_id: selectedCalendarId,
          month,
        }),
      });

      const data = await res.json();
      console.log("[FRONT] RAW RESPONSE =", data);

      if (!res.ok) {
        throw new Error(data.detail || data.error || `HTTP ${res.status}`);
      }

      setSlots(groupSlots(data.slots || []));
    } catch (err: any) {
      console.error("[FRONT][ERROR]", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  // Tab 2 — /plato/calendars
  // ─────────────────────────────────────────────
  const handleFetchCalendars = async (): Promise<CalendarItem[]> => {
    if (!selectedClientId) return [];

    setCalLoading(true);
    setCalError("");

    try {
      const res = await fetch(`${BACKEND}/plato/calendars`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ praticien_id: selectedClientId }),
      });

      const json = await res.json();
      console.info("[PlatoScraper][calendars]", json);

      if (!res.ok) {
        throw new Error(json?.detail || "Failed to load calendars");
      }

      const items: CalendarItem[] = Array.isArray(json?.items) ? json.items : [];
      setCalSource(json?.source || "");
      setCalItems(items);
      return items;
    } catch (err: any) {
      console.error(err);
      setCalItems([]);
      setCalSource("");
      setCalError(err?.message || "Unable to fetch calendars");
      return [];
    } finally {
      setCalLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  // Tab 2 — /plato/appointments/by-date
  // ─────────────────────────────────────────────
  const handleFetchAppointments = async () => {
    if (!selectedClientId || !apptDate) return;

    setApptLoading(true);
    setApptError("");

    try {
      const token = await getAuthToken();
      const res = await fetch(`${BACKEND}/plato/appointments/by-date`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token ?? "" },
        body: JSON.stringify({ praticien_id: selectedClientId, date: apptDate }),
      });

      const json = await res.json();
      console.info("[PlatoScraper][appointments]", json);

      if (!res.ok) {
        throw new Error(json?.detail || "Failed to load appointments");
      }

      setCalSource(json?.source || "");
      setApptAllowedColors(Array.isArray(json?.allowed_colors) ? json.allowed_colors : []);
      setApptItems(Array.isArray(json?.items) ? json.items : []);
    } catch (err: any) {
      console.error(err);
      setApptItems([]);
      setApptAllowedColors([]);
      setApptError(err?.message || "Unable to fetch appointments");
    } finally {
      setApptLoading(false);
    }
  };

  const handleFetchAppointmentDetail = async (appointmentId: string) => {
    setInspectedApptId(appointmentId);
    setApptDetailRaw(null);
    setApptDetailLoading(true);
    try {
      const res = await fetch(`${BACKEND}/plato/diagnostic/appointment-detail`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ praticien_id: selectedClientId, appointment_id: appointmentId }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "Failed to fetch detail");
      const rawPreview = json?.result?.raw_preview;
      const detail = Array.isArray(rawPreview) ? rawPreview[0] : rawPreview;
      setApptDetailRaw(detail || json?.result || null);
    } catch (err: any) {
      console.error("[PlatoScraper][appointment-detail]", err);
      setApptDetailRaw(null);
    } finally {
      setApptDetailLoading(false);
    }
  };

  const mergedAppointmentsRows = useMemo<MergedAppointmentRow[]>(() => {
    const grouped = new Map<string, MergedAppointmentRow>();

    apptItems.forEach((item, idx) => {
      const normalizedKey = normalizePatientKey(item.patient_name, item.phone);
      const fallbackKey = item.id || `appointment-${idx}`;
      const key = normalizedKey || fallbackKey;

      const startDate = parseDateSafely(item.start);
      const durationMinutes = getDurationMinutes(item.start, item.end);
      const calendarLabel = item.calendar_name || item.calendar_color || "-";

      if (!grouped.has(key)) {
        const statusKey = statusField
          ? `code_${statusField.charAt(0).toUpperCase() + statusField.slice(1)}`
          : "";
        grouped.set(key, {
          key,
          patient_name: item.patient_name || "-",
          phone: item.phone || "-",
          time: formatTime(item.start),
          sortStart: startDate ? startDate.toISOString() : "",
          durationMinutes,
          calendars: calendarLabel ? [calendarLabel] : ["-"],
          raw_id: item.id || undefined,
          raw_calendar_id: (item.raw as any)?.calendar_id || undefined,
          raw_patient_id: item.patient_id || undefined,
          raw_calendar_color: item.calendar_color || undefined,
          raw_status_code: statusKey ? String((item.raw as any)?.[statusKey] ?? "") : undefined,
        });
        return;
      }

      const existing = grouped.get(key);
      if (!existing) return;

      existing.durationMinutes += durationMinutes;

      if (startDate) {
        const existingDate = parseDateSafely(existing.sortStart);
        if (!existingDate || startDate.getTime() < existingDate.getTime()) {
          existing.sortStart = startDate.toISOString();
          existing.time = formatTime(item.start);
        }
      }

      if (calendarLabel && !existing.calendars.includes(calendarLabel)) {
        existing.calendars.push(calendarLabel);
      }
    });

    return Array.from(grouped.values()).sort((a, b) => {
      const aDate = parseDateSafely(a.sortStart);
      const bDate = parseDateSafely(b.sortStart);
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return aDate.getTime() - bDate.getTime();
    });
  }, [apptItems, statusField]);

  // ─────────────────────────────────────────────
  // Tab 2 — CRUD handlers
  // ─────────────────────────────────────────────

  const toDatetimeLocal = (iso?: string): string => {
    if (!iso) return "";
    const d = parseDateSafely(iso);
    if (!d) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const handleOpenCreate = async () => {
    const defaultStart = `${apptDate}T09:00`;
    const defaultEnd = `${apptDate}T09:30`;
    setApptFormMode("create");
    setEditingApptId(null);
    setApptFormError("");
    setApptFormOpen(true);

    const items = calItems.length > 0 ? calItems : await handleFetchCalendars();
    setApptFormData({
      calendar_id: items[0]?.id || "",
      color: (items[0]?.raw?.color as string) || "",
      patient_id: "",
      title: "",
      starttime: defaultStart,
      endtime: defaultEnd,
    });
  };

  const handleOpenEdit = (row: MergedAppointmentRow) => {
    setApptFormMode("edit");
    setEditingApptId(row.raw_id || null);
    setApptFormData({
      calendar_id: row.raw_calendar_id || calItems[0]?.id || "",
      color: row.raw_calendar_color || "",
      patient_id: row.raw_patient_id || "",
      title: row.patient_name !== "-" ? row.patient_name : "",
      starttime: toDatetimeLocal(row.sortStart),
      endtime: toDatetimeLocal(
        row.sortStart
          ? new Date(new Date(row.sortStart).getTime() + row.durationMinutes * 60000).toISOString()
          : ""
      ),
      status_code: row.raw_status_code || "",
    });
    setApptFormError("");
    setApptFormOpen(true);
  };

  const handleDeleteAppt = async (row: MergedAppointmentRow) => {
    if (!row.raw_id) {
      alert("Impossible de supprimer : ID rendez-vous inconnu.");
      return;
    }
    if (!window.confirm(`Supprimer le rendez-vous de ${row.patient_name} à ${row.time} ?`)) return;

    try {
      const token = await getAuthToken();
      const res = await fetch(`${BACKEND}/plato/appointment/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token ?? "" },
        body: JSON.stringify({
          praticien_id: selectedClientId,
          appointment_id: row.raw_id,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.detail || "Delete failed");
      await handleFetchAppointments();
    } catch (err: any) {
      alert(`Erreur suppression : ${err.message}`);
    }
  };

  const handleSubmitAppt = async () => {
    if (!apptFormData.patient_id.trim()) {
      setApptFormError("Patient ID is required.");
      return;
    }
    if (!apptFormData.color) {
      setApptFormError("Please select a calendar.");
      return;
    }

    setApptFormLoading(true);
    setApptFormError("");

    // Convertir "YYYY-MM-DDTHH:MM" → "YYYY-MM-DD HH:MM" pour Plato
    const toPlato = (dt: string) => dt.replace("T", " ");

    try {
      const token = await getAuthToken();
      if (apptFormMode === "create") {
        const res = await fetch(`${BACKEND}/plato/appointment/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token ?? "" },
          body: JSON.stringify({
            praticien_id: selectedClientId,
            calendar_id: apptFormData.calendar_id,
            patient_id: apptFormData.patient_id,
            title: apptFormData.title,
            starttime: toPlato(apptFormData.starttime),
            endtime: toPlato(apptFormData.endtime),
            color: apptFormData.color || undefined,
          }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json?.detail || "Create failed");
      } else {
        if (!editingApptId) throw new Error("ID rendez-vous manquant pour la mise à jour.");
        const res = await fetch(`${BACKEND}/plato/appointment/update`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token ?? "" },
          body: JSON.stringify({
            praticien_id: selectedClientId,
            appointment_id: editingApptId,
            calendar_id: apptFormData.calendar_id,
            patient_id: apptFormData.patient_id,
            title: apptFormData.title,
            starttime: toPlato(apptFormData.starttime),
            endtime: toPlato(apptFormData.endtime),
            color: apptFormData.color || undefined,
            status_code: apptFormData.status_code || undefined,
          }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json?.detail || "Update failed");
      }

      setApptFormOpen(false);
      await handleFetchAppointments();
    } catch (err: any) {
      setApptFormError(err.message);
    } finally {
      setApptFormLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  // Rendu
  // ─────────────────────────────────────────────
  if (!user) return <p>Login required</p>;
  if (user.role !== "admin") return <p>Forbidden</p>;

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-semibold">Plato API testing</h1>

      {/* CHOIX CLINIQUE */}
      <div className="max-w-lg space-y-2">
        <label className="block text-sm font-medium">Clinic</label>
        <select
          className="border px-3 py-2 w-full rounded"
          value={selectedClientId}
          onChange={(e) => handleSelectClient(e.target.value)}
        >
          <option value="">-- Choose a clinic --</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.raison_sociale}
            </option>
          ))}
        </select>
      </div>

      {/* INFO CONFIG PLATO */}
      {selectedClientId && (
        <div className="max-w-lg text-sm mt-2">
          {dbName && apiKey ? (
            <p>
              <b>Plato DB:</b> {dbName}
            </p>
          ) : (
            <p className="text-red-600">
              Aucune configuration Plato disponible pour cette clinique (ou
              generate-calendar non exécuté).
            </p>
          )}
        </div>
      )}

      {/* TABS */}
      {selectedClientId && (
        <div className="border-b flex gap-0 mt-2">
          <button
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === "slots"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("slots")}
          >
            Slot Tester
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm ${
              activeTab === "calendars"
                ? "border-b-2 border-blue-600 text-blue-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("calendars")}
          >
            Calendars &amp; Appointments
          </button>
        </div>
      )}

      {/* ══════════════════════════════════════════
          ONGLET 1 — SLOT TESTER
      ══════════════════════════════════════════ */}
      {activeTab === "slots" && (
        <div className="space-y-4">
          {/* BOUTON LOAD CONFIG */}
          {dbName && apiKey && (
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded"
              onClick={handleLoadOnlineConfig}
            >
              Load Online Booking Config
            </button>
          )}

          <button onClick={fetchBookingLink}>
            🔗 Get booking link
          </button>

          {bookingUrl && (
            <div style={{ marginTop: 12 }}>
              <strong>Booking link:</strong>
              <br />
              <a href={bookingUrl} target="_blank" rel="noopener noreferrer">
                {bookingUrl}
              </a>
            </div>
          )}

          {onlineRaw && (
            <div className="mt-4 p-4 border rounded bg-gray-50 text-sm">
              <h3 className="font-semibold mb-2">
                🔍 Online Booking – Raw Plato Response
              </h3>
              <pre className="overflow-x-auto text-xs">
                {JSON.stringify(onlineRaw, null, 2)}
              </pre>
            </div>
          )}

          {/* CALENDAR SELECT */}
          {onlineConfig && onlineConfig.calendars && onlineConfig.calendars.length > 0 ? (
            <div className="max-w-lg space-y-4 border rounded p-4 bg-white mt-4">
              <label className="text-sm font-medium">Calendar</label>
              <select
                className="border px-3 py-2 w-full rounded"
                value={selectedCalendarId}
                onChange={(e) => setSelectedCalendarId(e.target.value)}
              >
                {onlineConfig.calendars.map((cal) => (
                  <option key={cal.id} value={cal.id}>
                    {cal.name} ({cal.id})
                  </option>
                ))}
              </select>
            </div>
          ) : onlineConfig ? (
            <div className="text-red-600 mt-4">
              ⚠️ Aucun calendrier renvoyé par Plato (online booking désactivé).
            </div>
          ) : null}

          {/* DATE SELECTION */}
          {selectedCalendarId && (
            <div className="max-w-lg space-y-2 mt-4">
              <label className="block text-sm font-medium">Start Date</label>
              <input
                type="date"
                className="border px-3 py-2 w-full rounded"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
          )}

          {/* FETCH SLOTS */}
          {dbName && apiKey && selectedCalendarId && (
            <button
              className="bg-green-600 text-white px-4 py-2 rounded mt-4"
              onClick={handleFetchSlots}
              disabled={loading}
            >
              {loading ? "Fetching..." : "Fetch Slots"}
            </button>
          )}

          {error && <p className="text-red-500 mt-2">{error}</p>}

          {/* DISPLAY SLOTS — Doctor → Date → Times */}
          {slots && (
            <div className="mt-8">
              <h2 className="text-xl font-bold mb-4">Available Slots</h2>

              {Object.keys(slots).map((doctor) => (
                <div
                  key={doctor}
                  className="border rounded p-4 mb-6 shadow-sm bg-white"
                >
                  <h3 className="text-lg font-semibold mb-3 text-blue-600">
                    {doctor}
                  </h3>

                  {Object.keys(slots[doctor]).map((date) => (
                    <div key={date} className="mb-4">
                      <h4 className="font-medium">{date}</h4>

                      <div className="flex flex-wrap gap-2 mt-2">
                        {slots[doctor][date].map((t: string) => (
                          <span
                            key={t}
                            className="px-2 py-1 text-sm rounded bg-green-100 text-green-700 border"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════
          ONGLET 2 — CALENDARS & APPOINTMENTS
      ══════════════════════════════════════════ */}
      {activeTab === "calendars" && (
        <div className="space-y-5">
          {!selectedClientId && (
            <p className="text-sm text-gray-500">
              Sélectionnez d'abord une clinique ci-dessus.
            </p>
          )}

          {selectedClientId && (
            <>
              {/* ── Fetch Calendars ── */}
              <button
                onClick={handleFetchCalendars}
                disabled={calLoading}
                className="bg-slate-900 text-white px-4 py-2 rounded hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {calLoading ? "Loading..." : "Fetch calendars"}
              </button>

              {calSource && (
                <p className="text-xs text-gray-500">Source: {calSource}</p>
              )}

              {calError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded p-3">
                  {calError}
                </div>
              )}

              {!calLoading && !calError && (
                <p className="text-sm text-gray-600">
                  {calItems.length} calendar(s)
                </p>
              )}

              {!calLoading && !calError && calItems.length === 0 && (
                <p className="text-sm text-gray-500">No calendars found.</p>
              )}

              {!calLoading && calItems.length > 0 && (
                <div className="overflow-x-auto border rounded p-2">
                  <table className="min-w-full text-sm">
                    <thead className="border-b font-medium bg-gray-50">
                      <tr>
                        <th className="px-2 py-1 text-left">Calendar ID</th>
                        <th className="px-2 py-1 text-left">Calendar name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calItems.map((item, idx) => (
                        <tr
                          key={`${item.id || "item"}-${idx}`}
                          className="border-b last:border-0"
                        >
                          <td className="px-2 py-1">{item.id || "-"}</td>
                          <td className="px-2 py-1">{item.name || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── Fetch Appointments ── */}
              <div className="border-t pt-4 space-y-3">
                <h3 className="text-sm font-semibold text-slate-900">
                  Appointments by date
                </h3>

                <div className="flex items-end gap-3 flex-wrap">
                  <div>
                    <label className="block text-sm font-medium mb-1">Date</label>
                    <input
                      type="date"
                      value={apptDate}
                      onChange={(e) => setApptDate(e.target.value)}
                      className="border rounded p-2"
                    />
                  </div>
                  <button
                    onClick={handleFetchAppointments}
                    disabled={apptLoading || !apptDate}
                    className="bg-slate-900 text-white px-4 py-2 rounded hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {apptLoading ? "Loading..." : "Fetch appointments"}
                  </button>
                  <button
                    onClick={handleOpenCreate}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                  >
                    + New appointment
                  </button>
                </div>

                {/* ── Formulaire Create / Edit ── */}
                {apptFormOpen && (
                  <div className="border rounded p-4 bg-slate-50 space-y-3">
                    <h4 className="font-medium text-sm">
                      {apptFormMode === "create" ? "New appointment" : "Edit appointment"}
                    </h4>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">Calendar</label>
                        <select
                          value={apptFormData.calendar_id}
                          onChange={(e) => {
                            const sel = calItems.find(c => c.id === e.target.value);
                            setApptFormData((p) => ({
                              ...p,
                              calendar_id: e.target.value,
                              color: (sel?.raw?.color as string) || "",
                            }));
                          }}
                          className="w-full border rounded p-2 text-sm"
                        >
                          <option value="">-- Select --</option>
                          {calItems.map((c) => (
                            <option key={c.id} value={c.id}>{c.name} ({c.id})</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium mb-1">Patient ID</label>
                        <input
                          type="text"
                          value={apptFormData.patient_id}
                          onChange={(e) => setApptFormData((p) => ({ ...p, patient_id: e.target.value }))}
                          className="w-full border rounded p-2 text-sm"
                          placeholder="UUID Plato patient"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium mb-1">Patient name (title)</label>
                        <input
                          type="text"
                          value={apptFormData.title}
                          onChange={(e) => setApptFormData((p) => ({ ...p, title: e.target.value }))}
                          className="w-full border rounded p-2 text-sm"
                          placeholder="John Doe"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium mb-1">Start</label>
                        <input
                          type="datetime-local"
                          value={apptFormData.starttime}
                          onChange={(e) => setApptFormData((p) => ({ ...p, starttime: e.target.value }))}
                          className="w-full border rounded p-2 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium mb-1">End</label>
                        <input
                          type="datetime-local"
                          value={apptFormData.endtime}
                          onChange={(e) => setApptFormData((p) => ({ ...p, endtime: e.target.value }))}
                          className="w-full border rounded p-2 text-sm"
                        />
                      </div>

                      {apptFormMode === "edit" && Object.keys(statusMap).length > 0 && (
                        <div>
                          <label className="block text-xs font-medium mb-1">
                            Status (code_{statusField.charAt(0).toUpperCase() + statusField.slice(1)})
                          </label>
                          <select
                            value={apptFormData.status_code}
                            onChange={(e) => setApptFormData((p) => ({ ...p, status_code: e.target.value }))}
                            className="w-full border rounded p-2 text-sm"
                          >
                            <option value="">— Inchangé —</option>
                            {Object.entries(statusMap).map(([code, label]) => (
                              <option key={code} value={code}>{label} ({code})</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    {apptFormError && (
                      <p className="text-sm text-red-600">{apptFormError}</p>
                    )}

                    <div className="flex gap-2">
                      <button
                        onClick={handleSubmitAppt}
                        disabled={apptFormLoading}
                        className="bg-slate-900 text-white px-4 py-2 rounded hover:bg-slate-800 disabled:opacity-60 text-sm"
                      >
                        {apptFormLoading
                          ? "Saving..."
                          : apptFormMode === "create" ? "Create" : "Update"}
                      </button>
                      <button
                        onClick={() => setApptFormOpen(false)}
                        className="border px-4 py-2 rounded text-sm hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {apptAllowedColors.length > 0 && (
                  <p className="text-xs text-gray-500">
                    Allowed calendar colors: {apptAllowedColors.join(", ")}
                  </p>
                )}

                {apptError && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded p-3">
                    {apptError}
                  </div>
                )}

                {!apptLoading && !apptError && (
                  <p className="text-sm text-gray-600">
                    {mergedAppointmentsRows.length} appointment(s)
                  </p>
                )}

                {!apptLoading && apptItems.length > 0 && apptItems[0]?.raw && (
                  <details className="text-xs border rounded p-2 bg-gray-50">
                    <summary className="cursor-pointer text-gray-600 font-medium">
                      Raw keys of first appointment ({Object.keys(apptItems[0].raw).length} keys)
                    </summary>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {Object.keys(apptItems[0].raw).map((key) => {
                        const isCandidate = /status|state|etat|code|type|active|confirmed/i.test(key);
                        return (
                          <span
                            key={key}
                            className={`px-2 py-0.5 rounded text-xs font-mono border ${
                              isCandidate
                                ? "bg-yellow-100 border-yellow-300 text-yellow-800 font-bold"
                                : "bg-white border-gray-200 text-gray-600"
                            }`}
                          >
                            {key}
                          </span>
                        );
                      })}
                    </div>
                  </details>
                )}

                {!apptLoading && !apptError && mergedAppointmentsRows.length === 0 && (
                  <p className="text-sm text-gray-500">
                    No appointments found for this date.
                  </p>
                )}

                {!apptLoading && mergedAppointmentsRows.length > 0 && (
                  <div className="overflow-x-auto border rounded p-2">
                    <table className="min-w-full text-sm">
                      <thead className="border-b font-medium bg-gray-50">
                        <tr>
                          <th className="px-2 py-1 text-left">Time</th>
                          <th className="px-2 py-1 text-left">Patient</th>
                          <th className="px-2 py-1 text-left">Calendar</th>
                          <th className="px-2 py-1 text-left">Phone</th>
                          <th className="px-2 py-1 text-left">Duration</th>
                          <th className="px-2 py-1 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mergedAppointmentsRows.map((item) => (
                          <tr key={item.key} className="border-b last:border-0">
                            <td className="px-2 py-1">{item.time}</td>
                            <td className="px-2 py-1">{item.patient_name}</td>
                            <td className="px-2 py-1">
                              {item.calendars.length ? item.calendars.join(", ") : "-"}
                            </td>
                            <td className="px-2 py-1">{item.phone}</td>
                            <td className="px-2 py-1">
                              {formatDurationFromMinutes(item.durationMinutes)}
                            </td>
                            <td className="px-2 py-1">
                              <div className="flex gap-1">
                                <button
                                  onClick={() => handleOpenEdit(item)}
                                  className="text-xs px-2 py-1 border rounded hover:bg-slate-100"
                                  title="Edit"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteAppt(item)}
                                  className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50"
                                  title="Delete"
                                >
                                  Delete
                                </button>
                                <button
                                  onClick={() => handleFetchAppointmentDetail(item.raw_id || "")}
                                  disabled={!item.raw_id || apptDetailLoading}
                                  className="text-xs px-2 py-1 border border-blue-200 text-blue-600 rounded hover:bg-blue-50 disabled:opacity-40"
                                  title="Inspect raw fields"
                                >
                                  Inspect
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {apptDetailLoading && (
                  <p className="text-xs text-gray-500 mt-2">Fetching appointment detail...</p>
                )}

                {apptDetailRaw && (
                  <div className="border rounded p-4 bg-blue-50 space-y-3 mt-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-sm text-blue-900">
                        Raw Field Inspector — {inspectedApptId}
                      </h4>
                      <button
                        onClick={() => setApptDetailRaw(null)}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Close
                      </button>
                    </div>
                    <p className="text-xs text-blue-700">
                      Champs surlignés (jaune) = candidats status/type.
                    </p>
                    <div className="overflow-x-auto border rounded bg-white">
                      <table className="min-w-full text-xs">
                        <thead className="border-b bg-gray-50">
                          <tr>
                            <th className="px-3 py-1 text-left font-medium">Key</th>
                            <th className="px-3 py-1 text-left font-medium">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(apptDetailRaw).map(([key, value]) => {
                            const isCandidate = /status|state|etat|code|type|active|confirmed/i.test(key);
                            return (
                              <tr
                                key={key}
                                className={`border-b last:border-0 ${isCandidate ? "bg-yellow-50" : ""}`}
                              >
                                <td className={`px-3 py-1 font-mono ${isCandidate ? "font-bold text-yellow-800" : ""}`}>
                                  {key}
                                </td>
                                <td className="px-3 py-1 font-mono break-all">
                                  {typeof value === "object"
                                    ? JSON.stringify(value)
                                    : String(value ?? "")}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <details>
                      <summary className="text-xs text-blue-700 cursor-pointer">Full JSON</summary>
                      <pre className="text-xs mt-2 overflow-x-auto">
                        {JSON.stringify(apptDetailRaw, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
